import React from 'react';

import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import { Button } from 'modules/common/input';
import EmptyState from 'modules/common/components/EmptyState';
import PageLoader from 'modules/common/components/PageLoader';
import { parseNumber, formatCurrency } from 'modules/common/utils';

import { CartRowHeader } from '../../../services/components/cart/CartRow';
import Receipt from '../../../services/components/cart/Receipt';
import { useGetSelectedRecentTransaction } from '../hooks';

function OrderDetailsModal({ data, includes, onClose, onRefresh }) {
  const [isGetLoading, recentData] = useGetSelectedRecentTransaction({
    order_uuid: data?.uuid,
  });

  const receipt = {
    subtotal: () => recentData?.subtotal,
    discount: () => recentData?.total_discount,
    total: () => recentData?.total || 0,
    tax: () => recentData?.tax || 0,
    payment_method: recentData?.payment_method,
    amount_tendered: recentData?.amount_tendered,
    change: recentData?.change,
  };

  if (isGetLoading) {
    return (
      <div className="flex flex-col flex-1">
        <PageLoader bg="!transparent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 flex-1 overflow-hidden">
      <div className="flex gap-2">
        <div className="space-y-0.5 flex-1">
          <div className="text-2xl font-bold -ml-0.5">
            Order #{data?.order_number}
          </div>
          <div className="text-sm font-normal text-gray-500">
            <div>{dayjs(recentData?.created_at).format('ll hh:mm A')}</div>
          </div>
        </div>

        <div>
          <button
            type="button"
            tabIndex="-1"
            className="rounded-full transition duration-300 ease-in-out group outline-none group hover:bg-primary-500 p-1"
            onClick={onClose}
          >
            <svg
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-6 h-6 transition duration-300 ease-in-out group-hover:text-white text-primary-500 rounded-full"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="4"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      <hr className="border-gray-200" />

      <div className="flex flex-col flex-1 gap-3 overflow-hidden">
        <CartRowHeader withRemove={false} />

        <div className="flex flex-col gap-3 flex-1 overflow-y-auto overflow-x-hidden pt-2 pr-1">
          {recentData?.cart?.length > 0 ? (
            (recentData?.cart || [])?.map((item, key) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={key}>
                <div className="flex items-start gap-3 text-sm">
                  <div className="grid grid-cols-8 flex-1">
                    <div className="leading-tight col-span-6 flex gap-3.5 items-start">
                      <div className="font-semibold">{item?.quantity}x</div>

                      <div className="space-y-0">
                        <div>{item?.name || '-'}</div>

                        <div className="text-gray-400 text-xs">
                          <div className="capitalize">
                            {item?.variants?.code}
                          </div>
                          {item?.addons?.map((subItem, subKey) => (
                            <div
                              // eslint-disable-next-line react/no-array-index-key
                              key={subKey}
                            >
                              + {subItem?.name} ~{' '}
                              {formatCurrency(parseNumber(subItem?.price || 0))}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex-shrink-0 col-span-2 text-right leading-tight">
                      <div>{item?.price_human}</div>
                      {parseNumber(item?.discount || 0) > 0 && (
                        <div className="text-xs text-gray-500 line-through">
                          {formatCurrency(
                            parseNumber(item?.price) + item?.discount
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="m-auto">
              <EmptyState imageSize="h-32" labelSize="!text-base" />
            </div>
          )}
        </div>
      </div>

      <Receipt
        data={{
          ...includes,
          ...receipt,
        }}
        hrClassName="border-gray-200"
        withPaymentMethod
        subtotal={data?.subtotal}
        override={{
          total: data?.total,
          tax: data?.tax,
          discount: data?.discount,
        }}
      />

      <div className="pt-3 grid grid-cols-10 gap-3">
        <div className="col-span-7">
          <Button
            className="btn outline-light font-semibold w-full"
            label="Reprint Receipt"
            onClick={onRefresh}
          />
        </div>
        <div className="col-span-3">
          <Button
            className="btn primary font-semibold w-full"
            label="Close"
            onClick={onClose}
          />
        </div>
      </div>
    </div>
  );
}

OrderDetailsModal.defaultProps = {
  data: {},
  includes: {},
  onClose: () => {},
  onRefresh: () => {},
};

OrderDetailsModal.propTypes = {
  data: PropTypes.instanceOf(Object),
  includes: PropTypes.instanceOf(Object),
  onClose: PropTypes.instanceOf(Function),
  onRefresh: PropTypes.instanceOf(Function),
};

export default OrderDetailsModal;
